import { useCallback, useMemo } from 'react';
import { Link, Outlet, useHref, useSearchParams } from 'react-router-dom';
import { BarChart3, Building2, Clock, AlertTriangle } from 'lucide-react';
import FilterBar from './components/FilterBar';
import KpiCards from './components/KpiCards';
import { useRequestLogAnalytics } from '@/data/requestLog';
import { Error } from '@/components/ui/error';
import { Loading } from '@/components/ui/loading';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ALL_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
};

const getDefaultEndDate = () => {
  return new Date().toISOString().split('T')[0];
};

const Page = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pathname = useHref();

  const startDate = searchParams.get('startDate') || getDefaultStartDate();
  const endDate = searchParams.get('endDate') || getDefaultEndDate();
  const methodsParam = searchParams.get('methods');
  const methods = useMemo(() => (methodsParam ? methodsParam.split(',') : [...ALL_METHODS]), [methodsParam]);
  const routeGroup = searchParams.get('routeGroup') || 'all';
  const statusFilter = searchParams.get('status') || 'all';
  const source = searchParams.get('source') || 'all';

  const updateParam = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const analytics = useRequestLogAnalytics({
    startDate,
    endDate,
    methods,
    routeGroup,
    statusFilter,
    source
  });

  const handleMethodToggle = useCallback(
    (method) => {
      const updated = methods.includes(method) ? methods.filter((m) => m !== method) : [...methods, method];
      if (updated.length === 0) return;
      const isAll = ALL_METHODS.every((m) => updated.includes(m)) && updated.length === ALL_METHODS.length;
      updateParam('methods', isAll ? null : updated.join(','));
    },
    [methods, updateParam]
  );

  const handleReset = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  if (analytics.isLoading && !analytics.data) {
    return <Loading />;
  }

  if (analytics.error) {
    return <Error message="An error occurred loading request analytics" />;
  }

  const { kpis, volume_by_route, slowest_routes, top_errors, requests_by_company } = analytics.data;

  const search = searchParams.toString();
  const qs = search ? `?${search}` : '';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-semibold">Request Analytics</h1>
        <p className="text-sm text-muted-foreground">{kpis.total_requests.toLocaleString()} requests matched</p>
      </div>

      <FilterBar
        startDate={startDate}
        endDate={endDate}
        methods={methods}
        routeGroup={routeGroup}
        statusFilter={statusFilter}
        source={source}
        onStartDateChange={(v) => updateParam('startDate', v)}
        onEndDateChange={(v) => updateParam('endDate', v)}
        onMethodToggle={handleMethodToggle}
        onRouteGroupChange={(v) => updateParam('routeGroup', v === 'all' ? null : v)}
        onStatusFilterChange={(v) => updateParam('status', v === 'all' ? null : v)}
        onSourceChange={(v) => updateParam('source', v === 'all' ? null : v)}
        onReset={handleReset}
      />

      <KpiCards kpis={kpis} />

      <Tabs value={pathname} className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value={`/request-analytics${qs}`} asChild>
            <Link to={`/request-analytics${qs}`}>
              <BarChart3 />
              Volume
            </Link>
          </TabsTrigger>
          <TabsTrigger value={`/request-analytics/company-activity${qs}`} asChild>
            <Link to={`/request-analytics/company-activity${qs}`}>
              <Building2 />
              Company Activity
            </Link>
          </TabsTrigger>
          <TabsTrigger value={`/request-analytics/slowest-routes${qs}`} asChild>
            <Link to={`/request-analytics/slowest-routes${qs}`}>
              <Clock />
              Slowest Routes
            </Link>
          </TabsTrigger>
          <TabsTrigger value={`/request-analytics/errors${qs}`} asChild>
            <Link to={`/request-analytics/errors${qs}`}>
              <AlertTriangle />
              Errors
            </Link>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Outlet context={{ volume_by_route, slowest_routes, top_errors, requests_by_company }} />
    </div>
  );
};

export default Page;
